/**
 * IndexationAlertsBanner — Bandeau proactif : contrats indexés avec
 * révision passée/imminente + dérive €/an cumulée du portefeuille.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  TrendingUp, TrendingDown, ArrowRight, Loader2, Calendar, AlertCircle,
} from "lucide-react";
import { cn } from "@/lib/utils";

const fmtEUR = (v) => v == null ? "—" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR",
  { day: "2-digit", month: "short", year: "numeric" }) : "—";

const URG_META = {
  overdue: { cls: "bg-red-100 text-red-800 border-red-300", label: "En retard" },
  imminent: { cls: "bg-orange-100 text-orange-800 border-orange-300", label: "Imminent" },
  upcoming: { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "Prévu" },
};

export default function IndexationAlertsBanner({ compact = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/contracts/indexation-impact?window_days=90");
             setData(r.data); }
      catch { setData({ items: [] }); }
    })();
  }, []);

  if (!data) return (
    <Card className="p-3 text-xs text-slate-500 flex items-center gap-2">
      <Loader2 className="h-3 w-3 animate-spin" /> Analyse indexation…
    </Card>
  );
  if (!data.items?.length) return null;

  const drift = data.total_drift_year_ht || 0;
  const positive = drift >= 0;
  const TrendIcon = positive ? TrendingUp : TrendingDown;

  return (
    <Card className="border-purple-300 bg-gradient-to-r from-purple-50 to-fuchsia-50/50"
          data-testid="indexation-banner">
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <div className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0",
              positive ? "bg-red-500" : "bg-emerald-500")}>
              <TrendIcon className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">
                Dérive indexation portefeuille : <span className={positive ? "text-red-700" : "text-emerald-700"}>
                  {positive ? "+" : ""}{fmtEUR(drift)}/an
                </span>
              </div>
              <div className="text-[11px] text-slate-600">
                {data.count} contrat(s) impacté(s) · fenêtre 90 jours ·
                Indices : {Object.entries(data.by_index || {}).map(([k, v]) =>
                  `${k} (${v.count})`).join(", ")}
              </div>
            </div>
          </div>
        </div>

        <div className={cn("space-y-1.5 overflow-y-auto",
                            compact ? "max-h-52" : "max-h-96")}>
          {data.items.slice(0, compact ? 5 : 20).map((it) => (
            <button key={it.contract_id}
              onClick={() => navigate(`/app/contracts/${it.contract_id}`)}
              className="w-full flex items-center gap-2 bg-white rounded-lg border border-slate-200 hover:border-purple-400 hover:bg-purple-50/30 p-2.5 text-left transition"
              data-testid={`indexation-item-${it.contract_id}`}>
              <Badge className={cn("text-[10px] shrink-0", URG_META[it.urgency]?.cls)}>
                {URG_META[it.urgency]?.label}
              </Badge>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-slate-900 truncate">
                  {it.title || it.party_name}
                </div>
                <div className="text-[10px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                  <span>{it.party_name}</span>
                  <span>·</span>
                  <span>Indice {it.index_name} ({it.index_period})</span>
                  {it.next_revision_date && (
                    <>
                      <span>·</span>
                      <Calendar className="h-2.5 w-2.5 inline" />
                      <span>Rev {fmtDate(it.next_revision_date)}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className={cn("text-xs font-mono font-semibold",
                  it.drift_year_ht > 0 ? "text-red-700" : "text-emerald-700")}>
                  {it.drift_year_ht > 0 ? "+" : ""}{fmtEUR(it.drift_year_ht)}/an
                </div>
                <div className="text-[9px] text-slate-500 tabular-nums">
                  {it.drift_pct > 0 ? "+" : ""}{it.drift_pct}%
                </div>
              </div>
            </button>
          ))}
        </div>

        {data.items.length > (compact ? 5 : 20) && (
          <div className="mt-2 text-center">
            <button onClick={() => navigate("/app/contracts/indexation")}
              className="text-xs text-purple-700 hover:underline font-medium inline-flex items-center gap-1">
              Voir tout ({data.count}) <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}
